import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { PolarAngleAxis, PolarGrid, PolarRadiusAxis, Radar, RadarChart, ResponsiveContainer, Tooltip } from 'recharts';

const LABEL_FONT_SIZE = 12;
const LABEL_FONT_SIZE_SMALL = 10;
const LABEL_FONT_WEIGHT = 400;
const LINE_HEIGHT = 12;

/**
 * Calculate line height based on font size
 * Uses a ratio of approximately 1.3x for good readability
 */
function calculateLineHeight(fontSize: number): number {
  return Math.round(fontSize * 1.3);
}

type RadarDataPoint = {
  assessmentId?: string | null;
  fullName?: string;
  isPrimary?: boolean;
  label: string;
  value?: number;
  [key: string]: number | string | boolean | null | undefined;
};

type RadarSeries = {
  color: string;
  dataKey: string;
  fillColor: string;
  label: string;
  tooltipFormatter?: (params: { payload: RadarDataPoint; value: number }) => string;
};

type RadarChartComponentProps = {
  label?: string;
  labelFontSize?: number;
  layout?: 'default' | 'full-screen';
  maxValue?: number;
  nodes: RadarDataPoint[];
  onNodeClick?: (node: RadarDataPoint) => void;
  series?: RadarSeries[];
  size?: number;
};

const DEFAULT_SERIES: RadarSeries = {
  color: '#f59e0b',
  dataKey: 'value',
  fillColor: '#fcd34d',
  label: 'TRL',
};

export const RadarChartComponent: React.FC<RadarChartComponentProps> = (props) => {
  const {
    label = 'TRL',
    labelFontSize,
    layout = 'default',
    maxValue = 9,
    nodes,
    onNodeClick,
    series,
    size = 1,
  } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);
  const [labelTooltip, setLabelTooltip] = useState<{ node: RadarDataPoint; x: number; y: number } | null>(null);
  const [chartTooltip, setChartTooltip] = useState<{
    payload: Array<{ dataKey: string; name: string; payload: RadarDataPoint; value: number }>;
    x: number;
    y: number;
  } | null>(null);
  const mousePositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const tooltipUpdateRef = useRef<number | null>(null);

  const activeSeries = useMemo(() => {
    if (series && series.length > 0) {
      return series;
    }
    return [{ ...DEFAULT_SERIES, label }];
  }, [series, label]);

  const MIN_GRID_LINES = 4;
  const adjustedMax = maxValue % 2 === 0 ? maxValue : maxValue + 1;
  const domainMax = Math.max(adjustedMax, MIN_GRID_LINES);
  const isFullScreen = layout === 'full-screen';
  const chartMargin = isFullScreen
    ? { bottom: 40, left: 40, right: 40, top: 40 }
    : { bottom: 60, left: 60, right: 60, top: 60 };

  useEffect(() => {
    const tempSVG = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    tempSVG.style.position = 'absolute';
    tempSVG.style.visibility = 'hidden';
    document.body.appendChild(tempSVG);

    const tempText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    tempText.setAttribute('font-size', `${LABEL_FONT_SIZE}px`);
    tempText.setAttribute('font-weight', `${LABEL_FONT_WEIGHT}`);
    tempSVG.appendChild(tempText);

    document.body.removeChild(tempSVG);
    setIsReady(true);
  }, []);

  useEffect(() => {
    if (containerRef.current && isReady) {
      const svgElement = containerRef.current.querySelector('svg');
      if (svgElement) {
        svgElement.setAttribute('overflow', 'visible');
      }
    }
  }, [isReady]);

  useEffect(() => {
    return () => {
      if (tooltipUpdateRef.current !== null) {
        cancelAnimationFrame(tooltipUpdateRef.current);
      }
    };
  }, []);

  const buildTooltipPayload = (node: RadarDataPoint) =>
    activeSeries.map((series) => ({
      dataKey: series.dataKey,
      name: series.label,
      payload: node,
      value: typeof node[series.dataKey] === 'number' ? (node[series.dataKey] as number) : 0,
    }));

  const splitLabelIntoLines = (text: string): [string, string | null, string | null] => {
    const words = text.split(' ');
    if (words.length <= 1) {
      return [text, null, null];
    }

    if (words.length <= 3) {
      const midPoint = Math.ceil(words.length / 2);
      const firstLine = words.slice(0, midPoint).join(' ');
      const secondLine = words.slice(midPoint).join(' ');
      return [firstLine, secondLine, null];
    }

    const thirdPoint = Math.ceil(words.length / 3);
    const firstLine = words.slice(0, thirdPoint).join(' ');
    const secondLine = words.slice(thirdPoint, thirdPoint * 2).join(' ');
    const thirdLine = words.slice(thirdPoint * 2).join(' ');

    return [firstLine, secondLine, thirdLine];
  };

  const renderLabel = (renderProps: {
    payload: { index: number; value: string };
    textAnchor: 'end' | 'inherit' | 'middle' | 'start' | undefined;
    x: number;
    y: number;
  }): ReactElement<SVGElement> => {
    const { payload, textAnchor, x, y } = renderProps;
    const { index, value: labelText } = payload;

    if (index >= nodes.length) return <g />;

    const node = nodes[index];
    const hasAnyValue = activeSeries.some((s) => {
      const val = node[s.dataKey];
      return typeof val === 'number' && val > 0;
    });

    if (!hasAnyValue) return <g />;

    let labelX = x;
    let labelY = y;
    const offset = 4;

    if (textAnchor === 'start') {
      labelX = x + offset;
    } else if (textAnchor === 'end') {
      labelX = x - offset;
    } else if (y < 200) {
      labelY = y - offset;
    } else {
      labelY = y + offset;
    }

    const [firstLine, secondLine, thirdLine] = splitLabelIntoLines(labelText);
    const hasMultipleWords = labelText.includes(' ');
    const baseFontSize = labelFontSize ?? (hasMultipleWords ? LABEL_FONT_SIZE_SMALL : LABEL_FONT_SIZE);
    const fontSize = hasMultipleWords && !labelFontSize ? baseFontSize * 0.83 : baseFontSize;
    const lineHeight = calculateLineHeight(fontSize);

    const handleMouseEnter = (event: React.MouseEvent<SVGTextElement>) => {
      setLabelTooltip({ node, x: event.clientX, y: event.clientY });
    };

    const handleMouseLeave = () => setLabelTooltip(null);

    const handleClick = () => {
      if (onNodeClick && node.assessmentId) {
        onNodeClick(node);
      }
    };

    const isClickable = Boolean(onNodeClick && node.assessmentId);

    return (
      <g>
        <text
          fill="#1F1C1A"
          fontSize={fontSize}
          fontWeight={LABEL_FONT_WEIGHT}
          textAnchor={textAnchor}
          x={labelX}
          y={labelY}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ cursor: isClickable ? 'pointer' : 'default' }}
        >
          <tspan x={labelX}>{firstLine}</tspan>
          {secondLine && (
            <tspan
              dy={lineHeight}
              x={labelX}
            >
              {secondLine}
            </tspan>
          )}
          {thirdLine && (
            <tspan
              dy={lineHeight}
              x={labelX}
            >
              {thirdLine}
            </tspan>
          )}
        </text>
      </g>
    );
  };

  const renderTooltipContent = (
    payload: Array<{ dataKey: string; name: string; payload?: RadarDataPoint; value: number }>,
    nodeData: RadarDataPoint | undefined,
  ) => {
    if (!payload || payload.length === 0) return null;

    const domain = nodeData?.fullName || nodeData?.label;

    const hasAnyValue = payload.some((p) => p.value !== undefined && p.value > 0);
    if (!hasAnyValue) return null;

    return (
      <div className="flex flex-col gap-y-1 bg-white p-2 border border-gray-200 rounded shadow-sm">
        <div className="font-medium text-sm">{domain}</div>
        {payload.map((item) => {
          if (item.value === undefined || item.value <= 0) return null;
          const seriesConfig = activeSeries.find((s) => s.dataKey === item.dataKey);
          let formattedValue: string | number = item.value;
          if (seriesConfig?.tooltipFormatter && nodeData) {
            formattedValue = seriesConfig.tooltipFormatter({ payload: nodeData, value: item.value });
          } else if (typeof item.value === 'number') {
            formattedValue = Number(item.value.toFixed(1));
          }
          return (
            <div
              key={item.dataKey}
              className="text-xs"
              style={{ color: seriesConfig?.color || '#f59e0b' }}
            >
              {item.name}: {formattedValue}
            </div>
          );
        })}
      </div>
    );
  };

  /**
   * Custom tooltip handler for Recharts - captures tooltip data and triggers fixed-position tooltip
   * Uses requestAnimationFrame to defer state updates and avoid React warnings
   */
  const handleChartTooltip = (tooltipProps: {
    active?: boolean;
    payload?: Array<{ dataKey: string; name: string; payload: RadarDataPoint; value: number }>;
  }) => {
    const { active, payload } = tooltipProps;

    // Cancel any pending update
    if (tooltipUpdateRef.current !== null) {
      cancelAnimationFrame(tooltipUpdateRef.current);
    }

    // Defer state update to avoid updating during render
    tooltipUpdateRef.current = requestAnimationFrame(() => {
      if (active && payload && payload.length > 0) {
        const hasAnyValue = payload.some((p) => p.value !== undefined && p.value > 0);
        if (hasAnyValue) {
          setChartTooltip({
            payload,
            x: mousePositionRef.current.x,
            y: mousePositionRef.current.y,
          });
        } else {
          setChartTooltip(null);
        }
      } else {
        setChartTooltip(null);
      }
      tooltipUpdateRef.current = null;
    });

    return null; // Don't render anything inline
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    mousePositionRef.current = { x: event.clientX, y: event.clientY };
    if (chartTooltip) {
      setChartTooltip((prev) => (prev ? { ...prev, x: event.clientX, y: event.clientY } : null));
    }
  };

  const handleMouseLeave = () => {
    setChartTooltip(null);
  };

  const clampedSize = Math.max(0.1, Math.min(2, size));

  return (
    <>
      <div
        className="w-full flex items-center justify-center"
        ref={containerRef}
        style={{
          transform: `scale(${clampedSize})`,
          transformOrigin: 'center',
        }}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
      >
        <ResponsiveContainer
          aspect={isFullScreen ? 1 : 1.25}
          height="100%"
          width="100%"
        >
          <RadarChart
            cx="50%"
            cy="50%"
            data={nodes}
            margin={chartMargin}
            outerRadius={isFullScreen ? '75%' : '80%'}
          >
            <Tooltip content={handleChartTooltip} />
            <PolarGrid
              stroke="#9ca3af"
              strokeDasharray="3 3"
            />

            <PolarRadiusAxis
              domain={[0, domainMax]}
              stroke="transparent"
              tick={false}
              tickCount={domainMax / 2 + 1}
            />

            <PolarAngleAxis
              dataKey="label"
              tick={renderLabel}
            />

            {activeSeries.map((s) => (
              <Radar
                activeDot={
                  onNodeClick
                    ? {
                        cursor: 'pointer',
                        fill: s.color,
                        onClick: (_, payload) => {
                          const node = (payload as { payload?: RadarDataPoint })?.payload;
                          if (node?.assessmentId) {
                            onNodeClick(node);
                          }
                        },
                        r: 4,
                        stroke: s.color,
                        strokeWidth: 2,
                      }
                    : undefined
                }
                dataKey={s.dataKey}
                dot={{ fill: s.color, r: 2, stroke: s.color, strokeWidth: 1.5 }}
                fill={s.fillColor}
                fillOpacity={0.2}
                key={s.dataKey}
                name={s.label}
                stroke={s.color}
                strokeWidth={1.5}
              />
            ))}
          </RadarChart>
        </ResponsiveContainer>
      </div>
      {chartTooltip && (
        <div
          className="pointer-events-none fixed z-50"
          style={{ left: chartTooltip.x + 8, top: chartTooltip.y + 8 }}
        >
          {renderTooltipContent(chartTooltip.payload, chartTooltip.payload[0]?.payload)}
        </div>
      )}
      {labelTooltip && (
        <div
          className="pointer-events-none fixed z-50"
          style={{ left: labelTooltip.x + 8, top: labelTooltip.y + 8 }}
        >
          {renderTooltipContent(buildTooltipPayload(labelTooltip.node), labelTooltip.node)}
        </div>
      )}
    </>
  );
};
